import {
  BILLING_ERROR_CODES,
  isDirectPaymentOrderStatus,
  type DirectPaymentOrderStatus,
} from '@aysar/contracts';

import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

/** What this surface should say about a refusal, and what to offer next. */
export interface PaymentErrorCopy {
  /** A `payments:error.*` key. */
  i18nKey: string;
  /**
   * The status the api **found** when its claim refused — the only thing that
   * distinguishes five identical 409s from one another.
   *
   * `null` when the refusal is not a claim, or when the status it named is not
   * one this build knows: a newer api would otherwise put a raw enum value in
   * front of an operator, and `i18nKey` falls back to a sentence that does not
   * claim to know which way the order went.
   */
  foundStatus: DirectPaymentOrderStatus | null;
  /**
   * A colleague decided between this operator opening the order and acting on
   * it — and they were **right**: the order really was open when it was opened.
   *
   * So the answer is **re-read**, never retry. A retry would be refused again,
   * and on this surface the second attempt is the one that would look like a bug
   * rather than like somebody else's decision.
   */
  isConflict: boolean;
  /**
   * The order is not there any more (404).
   *
   * Distinct from a conflict, because there is nothing to go and read: the
   * dialog closes rather than leaving an operator looking at a row that no
   * longer exists.
   */
  isGone: boolean;
}

/**
 * The refusals this surface can meet, and the key that answers each.
 *
 * Codes come from the contracts constants rather than string literals (U3):
 * they are the same objects the api's `DomainError` subclasses read, so a rename
 * is a compile error instead of a silent fallback to "something went wrong".
 *
 * `billing_product_not_found` and the entitlement codes are deliberately absent
 * — nothing on the order desk can produce them, and mapping a code would be copy
 * nothing could ever show.
 */
const KEY_BY_CODE: Readonly<Record<string, string>> = {
  [BILLING_ERROR_CODES.orderNotClaimable]: 'error.notClaimable',
  [BILLING_ERROR_CODES.orderNotFound]: 'error.notFound',
  [BILLING_ERROR_CODES.orderPriceUnavailable]: 'error.priceUnavailable',
  [BILLING_ERROR_CODES.orderDiscountUnavailable]: 'error.discountUnavailable',
  // The receipt's three (P23b4). `orderEvidenceLocked` carries the status it
  // found, like `orderNotClaimable` does — "a colleague confirmed this" and
  // "this was refunded" send an operator to different places.
  [BILLING_ERROR_CODES.orderEvidencePresent]: 'error.evidencePresent',
  [BILLING_ERROR_CODES.orderEvidenceAbsent]: 'error.evidenceAbsent',
  [BILLING_ERROR_CODES.orderEvidenceLocked]: 'error.evidenceLocked',
};

/**
 * Turns an api refusal into what this surface says about it, or `null`.
 *
 * ## One code, five transitions — so the STATUS is the message
 *
 * `billing_order_not_claimable` is the refusal branch of submit, confirm,
 * reject, refund **and** discard, deliberately as one code: what an operator
 * needs to know is that somebody already decided, and the status says which way.
 * A generic "conflict" would tell them nothing they could act on, so this reads
 * `details.status[0]` and hands the caller a status to interpolate.
 *
 * `null` for anything unmapped, so the caller shows the api's own sentence
 * rather than a translation this module guessed at — which also keeps a
 * transport failure (`status: 0`) from being dressed up as a colleague's
 * decision.
 */
export function paymentErrorCopy(error: ApiError | null | undefined): PaymentErrorCopy | null {
  if (error === null || error === undefined) {
    return null;
  }

  const mapped = KEY_BY_CODE[error.code];

  if (mapped === undefined) {
    return null;
  }

  const foundStatus = readFoundStatus(error);
  const isClaim = error.code === BILLING_ERROR_CODES.orderNotClaimable;

  return {
    i18nKey: isClaim && foundStatus === null ? 'error.notClaimableUnknown' : mapped,
    foundStatus,
    // The STATUS decides, not the code alone (P20b). `orderPriceUnavailable` is
    // a 422 — nothing raced, the plan simply is not sold on this channel — and
    // offering "read it again" there would send an operator away from the picker
    // they have to change.
    isConflict: error.status === 409,
    isGone: error.status === 404,
  };
}

/**
 * `OrderNotClaimableError` puts the status it found under `details.status[0]`,
 * which is the only channel it has: the exception filter serializes `code` and
 * `details` and nothing else.
 */
function readFoundStatus(error: ApiError): DirectPaymentOrderStatus | null {
  const found = error.details?.['status']?.[0];

  return found !== undefined && isDirectPaymentOrderStatus(found) ? found : null;
}
